import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from '@Components/LegacyElixir/Footer';
import {
  useCustomPageSchema,
  useGetHeadernFooterState,
} from '@Providers/CustomPageSchemaProvider';
import styled from 'styled-components';
import debounce from 'lodash.debounce';
import { useConfig } from '@Src/providers/ConfigProvider';
import { PreviewBanner } from './LegacyElixir/Preview';
import { useDesignSchema } from '@Src/providers/DesignSchemaProvider';
import Header from './LegacyElixir/Header';
import HeaderV2 from './WebsiteV2/Header';
import FooterV2 from './WebsiteV2/Footer';

const StyledBody = styled.div`
  min-height: 65vh;
`;

const StyledContainer = styled.div<{ pageStyles?: string }>`
  ${(props) => props.pageStyles || ''}

  .order-online-h2 {
    text-shadow: 1px 1px 7px rgba(150, 150, 150, 1);
  }

  @media only screen and (max-width: 568px) {
    .restAddresses {
      line-height: 0.4;
    }
  }

  #flipdish-menu .has-feedback label ~ .form-control-feedback {
    top: 15px !important;
  }

  #skipNavigation {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
  }
`;

export const PageLayout = ({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}): JSX.Element => {
  const location = useLocation();
  const [showScrollToTopButton, setScrollToTopButton] =
    useState<boolean>(false);
  // removing `/` from pathname
  // `/index` -> `index`
  const path = location.pathname.substring(1);
  const pathKey = path === '' ? 'index' : path;
  const { header, footer } = useGetHeadernFooterState(pathKey);
  const pageSchema = useCustomPageSchema();
  const { schemaVersion } = useDesignSchema();
  const pageStyles = pageSchema[pathKey]?.pageStyles;

  const { isPreview } = useConfig();

  useEffect(() => {
    document.body.setAttribute('id', pathKey);
    document.body.classList.add(pathKey, 'default');
  }, [pathKey]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleScroll = (): void => {
    handleScrollToTopVisibility(window.pageYOffset);
  };

  const handleScrollToTopVisibility = useCallback(
    debounce((offset: number) => {
      setScrollToTopButton(offset > 7000);
    }, 200),
    [],
  );
  const handleScrollToTop = (): void => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  if (schemaVersion === 'v2') {
    return (
      <StyledContainer pageStyles={pageStyles} id="website-v2">
        {isPreview && <PreviewBanner />}
        <HeaderV2 />
        <StyledBody>{children}</StyledBody>
        <FooterV2 />
      </StyledContainer>
    );
  }
  return (
    <StyledContainer pageStyles={pageStyles} id="elixir">
      {isPreview && <PreviewBanner />}
      {header && <Header />}
      <StyledBody>{children}</StyledBody>
      {footer && <Footer />}
      <button
        onClick={handleScrollToTop}
        className={`cd-top ${showScrollToTopButton ? 'cd-is-visible' : ''}`}
      />
    </StyledContainer>
  );
};

export default PageLayout;
